import { useCallback, useRef, useState } from 'react';
import { TypedDocumentNode } from '@graphql-typed-document-node/core';
import type { FetchInfiniteQueryOptions, UsePrefetchQueryOptions } from '@tanstack/react-query';
import {
    GetNextPageParamFunction,
    hashKey,
    InfiniteData,
    useInfiniteQuery,
    UseInfiniteQueryOptions,
    useQueries,
    useQuery,
    UseQueryOptions,
} from '@tanstack/react-query';
import { deleteCookie, hasCookie } from 'cookies-next';
import { DocumentNode, print } from 'graphql';
import type { RequestOptions, Variables } from 'graphql-request';
import { GraphQLClient, rawRequest, request } from 'graphql-request';
import type { IncomingHttpHeaders, IncomingMessage } from 'node:http';

import {
    HASH_EXTENSION_KEY,
    PERSISTED_QUERY_NOT_FOUND,
} from '@/graphql/middleware/persisted-documents';
import type { Response } from '@/node_modules/graphql-request/build/cjs/types';
import {
    CLIENT_REGION_HEADER,
    CMS_OVERRIDES_HEADER,
    LOGIN_SESSION_COOKIE,
    MAX_GET_URL_LENGTH,
    REGION_IN_PATH,
    UNDEFINED,
} from '@/utils/constants/common';
import { getDomain, MfeNames } from '@/utils/domains.js';
import { currentEnvMirrorsProd } from '@/utils/getCurrentEnv';
import { getGraphQLPath, resolveGraphQLAppName } from '@/utils/graphql-path';
import handleDataLayerError from '@/utils/handleDataLayerError';
import { getHeaderValue } from '@/utils/server/request-extractor';

import { appLogger } from './app-logger/appLogger';
import {
    getInProcessExecutorFor,
    type InProcessGraphQLExecutor,
} from './server/in-process-graphql';
import { AUTOMATION_PLATFORM_HEADER } from './server/isAutomationRequest';
import { getRegion } from './server/region-utils';

// print() walks the full GraphQL AST on every call. Cache per document node (module-level singletons).
const printedQueryCache = new WeakMap<DocumentNode, string>();
const cachedPrint = (query: DocumentNode): string => {
    const cached = printedQueryCache.get(query);
    if (cached !== undefined) return cached;
    const result = print(query);
    printedQueryCache.set(query, result);
    return result;
};

export const REQUEST_TYPES = {
    POST: 'POST',
    GET: 'GET',
} as const;

export type RequestType = (typeof REQUEST_TYPES)[keyof typeof REQUEST_TYPES];

type ApiError = {
    // HTTP status of the server reply. graphql-request populates `response` (with
    // this status) for *any* server response — including 5xx/CDN error pages that
    // carry no GraphQL `errors` array. A genuine transport failure (aborted or
    // failed fetch) throws with no `response` at all, so the presence/absence of
    // `response` is what separates a benign abort from a real upstream error.
    status?: number;
    // GraphQL errors are a 0..N array (not a single-element tuple), and both
    // `extensions` and `extensions.code` are optional per the GraphQL spec — the
    // code below already reads them defensively.
    errors?: Array<{ message?: string; extensions?: { code?: string } }>;
};

type UseQueryError = {
    response?: ApiError;
    // Set by attachUrlToError on any error thrown by the actual GraphQL request
    // (both transport failures and ClientErrors). Its presence proves the failure
    // originated from the network call rather than a synchronous bug thrown before
    // the request, so a response-less error is only a benign transport abort when
    // it is tagged.
    requestUrl?: string;
};

type FoundationInfiniteQueryOptions<TData, TPageParam = unknown> = UseInfiniteQueryOptions<
    TData,
    UseQueryError,
    InfiniteData<TData, TPageParam>,
    readonly unknown[],
    TPageParam
>;

type InfiniteRequestOptions<TData, TPageParam = unknown> = Omit<
    FoundationInfiniteQueryOptions<TData, TPageParam>,
    'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'
>;

type InfiniteQueryPaginationOptions<TData, TPageParam = unknown> = Pick<
    FoundationInfiniteQueryOptions<TData, TPageParam>,
    'initialPageParam' | 'getNextPageParam'
>;

type GraphQLInfiniteRequestCore<
    TData,
    TQueryKeyParams extends object,
    TVariables extends TQueryKeyParams,
    TPageParam,
> = {
    type?: RequestType;
    cacheKey: string;
    query: TypedDocumentNode<TData, TVariables>;
    /** Params that identify this infinite query in the cache (exclude page fields such as `begin`). */
    queryKeyParams?: TQueryKeyParams | null;
    buildDataParams: (pageParam: TPageParam) => TVariables;
    appName?: MfeNames;
} & InfiniteQueryPaginationOptions<TData, TPageParam>;

export interface requestParams<TData, TVariables extends object = Record<string, unknown>> {
    type?: RequestType;
    cacheKey: string | null;
    query: TypedDocumentNode<TData, TVariables>;
    dataParams?: TVariables | null;
    appName?: MfeNames;
    requestOptions?: Omit<UseQueryOptions<TData, UseQueryError>, 'queryKey'>;
    /**
     * Opt-in request cancellation. When `true`, the query forwards React Query's
     * `AbortSignal` to the transport, so a superseded (queryKey change) or unmounted
     * request is aborted on the wire instead of running to completion. Leave it
     * `false` (the default) for reads that should finish regardless; enable it only
     * for rapidly-superseded reads like an as-you-type search, where stale in-flight
     * requests are pure waste. No effect on SSR prefetch or paginated paths.
     */
    cancelOnSupersede?: boolean;
    /**
     * Extra headers read each time the request is sent, so a value that changes over time (e.g. the
     * session JWT) goes out current without re-keying the query or refetching. Client-side only.
     */
    getHeaders?: () => Record<string, string>;
}

export interface prefetchRequestParams<TData, TVariables extends object = Record<string, unknown>> {
    type?: RequestType;
    cacheKey: string | null;
    query: TypedDocumentNode<TData, TVariables>;
    dataParams?: TVariables | null;
    appName?: MfeNames;
    req: IncomingMessage;
    region?: string;
}

/** Optional upstream pagination metadata carried on resolved pages. */
export type RequestPagePaginationMeta = {
    rowCount?: number;
    skippedCount?: number;
    hasError?: boolean;
    /** Consecutive `hasError` results for this page, set only by callers that track the streak. */
    errorCount?: number;
};

export type ResolvedRequestPage<T> = {
    items: T[];
    totalCount: number;
    isFetching?: false | never;
} & RequestPagePaginationMeta;

export type RequestPage<T> = { pageNumber: number } & (
    | ResolvedRequestPage<T>
    | { isFetching: true; totalCount?: never; items?: never; hasError?: never; errorCount?: never }
);

/**
 * Clear the client login cookie only when GraphQL reports an explicit auth failure.
 * Do **not** clear on `INTERNAL_SERVER_ERROR`: many reads can 500 transiently (e.g. right after
 * updateEmail), and deleting `UP_NEW_SESSION` incorrectly logs the user out.
 */
const GRAPHQL_CODE_THAT_CLEARS_LOGIN_SESSION = 'UNAUTHENTICATED';

export type RequestUrlLengthParams<TData, TVariables extends object = Record<string, unknown>> = {
    query: TypedDocumentNode<TData, TVariables>;
    params: TVariables;
    appName: MfeNames;
    relativePath?: boolean;
    isServer?: boolean;
};

export { getGraphQLPath, resolveGraphQLAppName } from '@/utils/graphql-path';

/** SSR / absolute GraphQL origin — aliases (e.g. hubs, account → app-shell) apply to host and path. */
const getGraphQLRequestOrigin = (
    appName: MfeNames,
    relativePath: boolean,
    isServer: boolean,
): string => (relativePath ? '' : getDomain(resolveGraphQLAppName(appName), undefined, isServer));

/**
 * Extract the persisted-document sha256 that codegen's `client` preset attaches
 * to every generated `TypedDocumentNode`. When present, callers send the hash
 * instead of the operation body — resistant to MFE bundle skew.
 */
const getPersistedHash = (doc: unknown): string | undefined => {
    const meta = (doc as { __meta__?: { hash?: unknown } } | null)?.__meta__;
    return typeof meta?.hash === 'string' ? meta.hash : undefined;
};

/**
 * Operation name for persisted-hash requests so Apollo can identify the operation
 * without the full body. Undefined for fragments or malformed nodes.
 */
const getOperationName = (doc: unknown): string | undefined => {
    const definitions = (doc as { definitions?: unknown[] } | null)?.definitions;
    if (!Array.isArray(definitions)) return undefined;
    for (const def of definitions) {
        const d = def as { kind?: string; name?: { value?: string } };
        if (d.kind === 'OperationDefinition' && typeof d.name?.value === 'string') {
            return d.name.value;
        }
    }
    return undefined;
};

/**
 * True for a full `DocumentNode` that `print()` can serialize. Codegen's
 * `embedHashInDocument` mode keeps the complete AST on every generated node
 * (the hash lives alongside it in `__meta__`), so hashed documents normally
 * pass this too — it only fails for hash-only or hand-built malformed nodes.
 */
const isPrintableDocumentNode = (doc: unknown): boolean =>
    doc != null &&
    typeof doc === 'object' &&
    (doc as { kind?: string }).kind === 'Document' &&
    Array.isArray((doc as { definitions?: unknown[] }).definitions);

/**
 * Headers that satisfy Apollo Server's default CSRF prevention independent of
 * `Content-Type`. A CDN/edge can strip `Content-Type` from a body-less GET before
 * it reaches Apollo — the request then looks "simple" and is rejected as potential
 * CSRF. These dedicated headers are forwarded untouched and clear the check on
 * their own; `x-apollo-operation-name` is added when the operation name is known.
 */
const buildApolloCsrfHeaders = (operationName?: string): Record<string, string> => ({
    'apollo-require-preflight': 'true',
    ...(operationName ? { 'x-apollo-operation-name': operationName } : {}),
});

/**
 * Assert `doc` is a real `DocumentNode` or carries a persisted-query hash.
 * Throws synchronously so an empty `{}` from the codegen fallback never reaches
 * the wire as an opaque BAD_REQUEST.
 */
const assertValidDocument = (
    doc: unknown,
    context: { cacheKey: string | null; appName: MfeNames },
): void => {
    if (getPersistedHash(doc)) return;
    if (isPrintableDocumentNode(doc)) return;
    throw new Error(
        `graphql(): unknown document for cacheKey="${context.cacheKey ?? 'default'}", appName="${context.appName}". ` +
            `Probable MFE bundle skew — the query template is not in the current \`documents\` map, or codegen ` +
            `has not been run for this source. Rebuild MFE bundles from a single source tree.`,
    );
};

const estimateGetUrlLength = <TData, TVariables extends object = Record<string, unknown>>({
    query,
    params,
    appName,
    relativePath = true,
    isServer = false,
}: RequestUrlLengthParams<TData, TVariables>): number => {
    // `assertValidDocument` ran upstream in every caller, so a `cachedPrint`
    // failure here is a real bug — do NOT swallow. The old try/catch let empty
    // POSTs reach Apollo as BAD_REQUEST.
    const queryString = cachedPrint(query);
    const variablesString = params ? JSON.stringify(params) : '{}';
    const graphQLPath = getGraphQLPath(appName);
    const baseUrl = `${getGraphQLRequestOrigin(appName, relativePath, isServer)}${graphQLPath}`;

    const searchParams = new URLSearchParams();
    searchParams.set('query', queryString);
    searchParams.set('variables', variablesString);
    // operationName is optional; using an empty string keeps the estimate simple while
    // still accounting for the parameter name and delimiters in the URL length.
    searchParams.set('operationName', '');

    const queryStringLength = searchParams.toString().length;

    return baseUrl.length + 1 + queryStringLength; // +1 for the leading "?" before the query string
};

// Narrow GraphQL results to exclude any union members with __typename ending in "Error"
type SuccessResult<T> = T extends { __typename: infer N }
    ? N extends string
        ? N extends `${string}Error`
            ? never
            : { [K in keyof T]: SuccessResult<T[K]> }
        : { [K in keyof T]: SuccessResult<T[K]> }
    : T extends (infer U)[]
      ? SuccessResult<U>[]
      : T extends object
        ? { [K in keyof T]: SuccessResult<T[K]> }
        : T;

// Build Client for Get Requests.
// Keyed by `${appName}:${relativePath}:${isServer}` — one instance per unique config tuple.
const graphQLClientCache = new Map<string, GraphQLClient>();
// Exported for test teardown only — allows jest.clearAllMocks() to reset constructor assertions.
export const _clearGraphQLClientCacheForTests = () => graphQLClientCache.clear();
const buildGraphQLClient = (appName: MfeNames, relativePath = true, isServer = false) => {
    const cacheKey = `${appName}:${relativePath}:${isServer}`;
    const cached = graphQLClientCache.get(cacheKey);
    if (cached) return cached;
    const graphQLPath = getGraphQLPath(appName);
    const client = new GraphQLClient(
        `${getGraphQLRequestOrigin(appName, relativePath, isServer)}${graphQLPath}`,
        {
            method: `GET`,
            headers: {
                'Content-Type': 'application/json',
                // Body-less GETs can have their Content-Type stripped by a CDN/edge,
                // which trips Apollo Server's CSRF prevention. Share the one CSRF-header
                // definition with the persisted path (no operation name is known here) so
                // the two GET transports can't drift when the contract changes.
                ...buildApolloCsrfHeaders(),
            },
            jsonSerializer: {
                parse: JSON.parse,
                stringify: JSON.stringify,
            },
        },
    );
    graphQLClientCache.set(cacheKey, client);
    return client;
};

// Header values only accept the Fetch-spec byte set: TAB, 0x20-0x7E, 0x80-0xFF. Everything
// else is percent-encoded: code points > 0xFF, and CR/LF/NUL/DEL, which are <= 0xFF but
// still invalid. Latin-1 bytes it accepts (e.g. é = 0xE9) pass through raw. Never throws.
const LATIN1_MAX_CODE_POINT = 0xff;
const MIN_PRINTABLE_CODE_POINT = 0x20;
const DEL_CODE_POINT = 0x7f;
const TAB_CODE_POINT = 0x09;
// '�' (U+FFFD) pre-encoded: fallback for unpaired surrogates that break encodeURIComponent.
const REPLACEMENT_CHAR_ENCODED = '%EF%BF%BD';

// True for code points that are valid header-value bytes per the Fetch spec.
const isHeaderSafeCodePoint = (code: number): boolean =>
    code <= LATIN1_MAX_CODE_POINT &&
    code !== DEL_CODE_POINT &&
    (code >= MIN_PRINTABLE_CODE_POINT || code === TAB_CODE_POINT);

export const toLatin1SafeHeaderValue = (value: string): string => {
    let sanitized = '';
    let mutated = false;
    for (const char of value) {
        // for...of yields whole code points; a non-empty char always has one at index 0.
        if (isHeaderSafeCodePoint(char.codePointAt(0)!)) {
            sanitized += char;
            continue;
        }
        mutated = true;
        try {
            sanitized += encodeURIComponent(char);
        } catch {
            sanitized += REPLACEMENT_CHAR_ENCODED; // unpaired surrogate
        }
    }
    return mutated ? sanitized : value;
};

// Helper function to create request headers
const createRequestHeaders = (
    cacheKey: string | null,
    additionalHeaders: Record<string, string> = {},
): Record<string, string> => {
    const headers: Record<string, string> = {
        'request-source': cacheKey || 'default',
        ...additionalHeaders,
    };
    // Force every value Latin-1-safe so no caller can throw at the transport.
    for (const key of Object.keys(headers)) {
        const value = headers[key];
        if (typeof value === 'string') {
            headers[key] = toLatin1SafeHeaderValue(value);
        }
    }
    return headers;
};

/**
 * Resolves the client-facing host from the incoming request for forwarding via x-forwarded-host.
 * Prefers a non-empty x-forwarded-host (set by upstream proxies) over the host header.
 * Returns undefined when neither header yields a usable value so callers can omit
 * the header entirely rather than sending an empty string.
 */
const getForwardedHost = (req: { headers: IncomingHttpHeaders }): string | undefined => {
    const normalizeHeader = (value: string | string[] | undefined): string | undefined => {
        if (value === undefined) return undefined;
        const first = Array.isArray(value) ? value[0] : value;
        if (first === undefined) return undefined;
        const trimmed = first.trim();
        return trimmed === '' ? undefined : trimmed;
    };
    return normalizeHeader(req.headers['x-forwarded-host']) ?? normalizeHeader(req.headers.host);
};

const processDataParams = (dataParams: unknown): Record<string, unknown> => {
    if (!dataParams) return {};
    return dataParams as Record<string, unknown>;
};

export type DoRequestParams<TData, TVariables extends object = Record<string, unknown>> = {
    appName: MfeNames;
    query: TypedDocumentNode<TData, TVariables>;
    params: TVariables;
    headers: Record<string, string>;
    isServer?: boolean;
    relativePath?: boolean;
    signal?: AbortSignal;
};

/**
 * Attaches the GraphQL endpoint URL to errors thrown by graphql-request,
 * since ClientError only includes query/variables but not the target URL.
 */
const attachUrlToError = (error: unknown, url: string): never => {
    if (error && typeof error === 'object') {
        (error as Record<string, unknown>).requestUrl = url;
    }
    throw error;
};

export const SSR_PREFETCH_TIMEOUT_MS_DEFAULT = 3000;

/**
 * Budget for one server-side GraphQL HTTP fetch, from `SSR_PREFETCH_TIMEOUT_MS`. Empty or
 * non-digit values fall back to the default; `0` disables the timeout. Server renders used to
 * wait on self-requests to the pod's own service for as long as the socket lived, which is how a
 * slow route turned into a render pile-up and a crash loop.
 */
export const resolveSsrPrefetchTimeoutMs = (): number => {
    const raw = (process.env.SSR_PREFETCH_TIMEOUT_MS ?? '').trim();
    return /^\d+$/.test(raw) ? Number(raw) : SSR_PREFETCH_TIMEOUT_MS_DEFAULT;
};

// An explicit timer rather than the built-in timeout signal, so Jest's fake timers drive it;
// unref keeps it from holding a worker open.
export const createTimeoutSignal = (ms: number): { signal: AbortSignal; clear: () => void } => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ms);
    (timer as { unref?: () => void }).unref?.();
    return { signal: controller.signal, clear: () => clearTimeout(timer) };
};

type ArmedTimeout = { signal: AbortSignal; clear: () => void; timeoutMs: number };

// graphql-request ships its own structural AbortSignal type, unrelated to the global one.
type TransportSignal = RequestOptions['signal'];
const toTransportSignal = (signal: AbortSignal | undefined): TransportSignal =>
    signal as unknown as TransportSignal;

/** Arms the SSR budget for a server-side fetch that was not handed a signal by its caller. */
const armSsrTimeout = (isServer: boolean, signal: AbortSignal | undefined): ArmedTimeout | null => {
    if (!isServer || signal) return null;
    const timeoutMs = resolveSsrPrefetchTimeoutMs();
    if (timeoutMs <= 0) return null;
    return { ...createTimeoutSignal(timeoutMs), timeoutMs };
};

const TIMEOUT_WARN_WINDOW_MS = 60_000;
const SSR_TIMEOUT_ERROR_NAME = 'TimeoutError';

// One warn per operation per window: during an incident every render times out on the same few
// operations, and a warn per timeout would be thousands of lines a second.
const lastTimeoutWarnAt = new Map<string, number>();

/** @internal — test hook; do not import from application code. */
export const __resetTimeoutWarnWindowForTests = (): void => {
    lastTimeoutWarnAt.clear();
};

type TimeoutErrorContext = {
    appName: MfeNames;
    operationName: string;
    timeoutMs: number;
    requestUrl: string;
};

const buildTimeoutError = ({
    appName,
    operationName,
    timeoutMs,
    requestUrl,
}: TimeoutErrorContext): Error => {
    const key = `${appName}:${operationName}`;
    const now = Date.now();
    const lastWarnAt = lastTimeoutWarnAt.get(key);
    if (lastWarnAt === undefined || now - lastWarnAt >= TIMEOUT_WARN_WINDOW_MS) {
        lastTimeoutWarnAt.set(key, now);
        appLogger.warn('SSR prefetch timed out', {
            component: 'request',
            appName,
            operationName,
            timeoutMs,
            requestUrl,
        });
    }
    return Object.assign(new Error(`SSR prefetch timed out after ${timeoutMs} ms`), {
        name: SSR_TIMEOUT_ERROR_NAME,
        requestUrl,
    });
};

const isSsrTimeoutError = (error: unknown): boolean =>
    error instanceof Error && error.name === SSR_TIMEOUT_ERROR_NAME;

// Helper function to perform POST requests. Arms the SSR budget itself for server-side callers
// that bypass executeRequest; a signal passed in belongs to the caller, which owns its translation.
export const doPostRequest = async <TData, TVariables extends object = Record<string, unknown>>({
    appName,
    query,
    params,
    headers,
    isServer = false,
    signal,
}: DoRequestParams<TData, TVariables>): Promise<TData> => {
    const baseUrl = isServer ? getGraphQLRequestOrigin(appName, false, true) : '';
    const graphQLPath = getGraphQLPath(appName);
    const url = `${baseUrl}${graphQLPath}`;
    const armed = armSsrTimeout(isServer, signal);
    try {
        return await request<TData>({
            url,
            document: query,
            variables: params as Variables,
            requestHeaders: headers,
            signal: toTransportSignal(signal ?? armed?.signal),
        });
    } catch (error) {
        if (armed?.signal.aborted) {
            throw buildTimeoutError({
                appName,
                operationName: getOperationName(query) ?? 'anonymous',
                timeoutMs: armed.timeoutMs,
                requestUrl: url,
            });
        }
        return attachUrlToError(error, url);
    } finally {
        armed?.clear();
    }
};

/**
 *
 * Unlike `request()` which returns only parsed data (`Promise<T>`), `rawRequest()` returns
 * the full HTTP response (`Promise<Response<T>>`) with `data`, `headers`, and `status`.
 * Use this when you need to access response headers (e.g., `Set-Cookie` for SSR cookie handling).
 *
 * @returns Promise<Response<T>> - Full response object with data, headers, and status
 *
 * Note: PTV Appshell Endpoint must use `app-shell` to match the GraphQL endpoint routing.
 */
export const doPostRawRequest = <TData, TVariables extends object = Record<string, unknown>>({
    appName = (process.env.NEXT_PUBLIC_APP_NAME as MfeNames) || 'app-shell',
    query,
    params,
    headers,
    isServer = false,
}: DoRequestParams<TData, TVariables>): Promise<Response<TData>> => {
    // `doPostRawRequest` bypasses executeRequest (callers need the raw response
    // for Set-Cookie forwarding etc.), so guard here directly.
    assertValidDocument(query, { cacheKey: null, appName });
    const baseUrl = isServer ? getGraphQLRequestOrigin(appName, false, true) : '';
    const queryString = cachedPrint(query);
    const graphQLPath = getGraphQLPath(appName);
    const url = `${baseUrl}${graphQLPath}`;

    return rawRequest<TData>(url, queryString, params as Variables, headers).catch((err) =>
        attachUrlToError(err, url),
    );
};

// Helper function to perform GET requests
const doGetRequest = <TData, TVariables extends object = Record<string, unknown>>({
    appName,
    query,
    params,
    headers,
    relativePath = true,
    isServer = false,
    signal,
}: DoRequestParams<TData, TVariables>) => {
    const graphQLClient = buildGraphQLClient(appName, relativePath, isServer);
    const graphQLPath = getGraphQLPath(appName);
    const url = `${getGraphQLRequestOrigin(appName, relativePath, isServer)}${graphQLPath}`;
    return graphQLClient
        .request<TData>({
            document: query,
            variables: params as Variables,
            requestHeaders: headers,
            signal: toTransportSignal(signal),
        })
        .catch((err) => attachUrlToError(err, url));
};

type DoPersistedRequestParams = {
    appName: MfeNames;
    hash: string;
    operationName: string | undefined;
    params: Record<string, unknown>;
    headers: Record<string, string>;
    relativePath?: boolean;
    isServer?: boolean;
    signal?: AbortSignal;
};

/**
 * Send only the codegen-emitted sha256 under `extensions[HASH_EXTENSION_KEY]` —
 * deliberately not Apollo's `persistedQuery.sha256Hash` shape (Apollo Server
 * would intercept that before our middleware runs). Our server-side middleware
 * resolves the hash from the manifest and hydrates `request.query`.
 *
 * Uses `fetch` directly since `graphql-request` 5.2.0 has no APQ support.
 * Emits the `{ response: { errors } }` shape `handleRequestError` expects.
 */
const doPersistedRequest = async <TData>({
    appName,
    hash,
    operationName,
    params,
    headers,
    relativePath = true,
    isServer = false,
    signal,
}: DoPersistedRequestParams): Promise<TData> => {
    const graphQLPath = getGraphQLPath(appName);
    const url = `${getGraphQLRequestOrigin(appName, relativePath, isServer)}${graphQLPath}`;

    // Bespoke extension key — Apollo's built-in APQ detector matches on
    // `persistedQuery.sha256Hash` and fires before any handler wrapper. Using
    // our own top-level key sidesteps that; the server middleware reads the
    // same constant, so the two ends stay in sync.
    const extensions = { [HASH_EXTENSION_KEY]: hash };
    const searchParams = new URLSearchParams();

    if (operationName) {
        searchParams.set('operationName', operationName);
    }

    if (params && Object.keys(params).length) {
        searchParams.set('variables', JSON.stringify(params));
    }

    searchParams.set('extensions', JSON.stringify(extensions));

    const searchString = searchParams.toString();
    const useGet = url.length + 1 + searchString.length <= MAX_GET_URL_LENGTH;

    // Satisfy Apollo Server's default CSRF prevention: a non-simple Content-Type
    // plus the dedicated preflight headers that survive a CDN/edge stripping
    // Content-Type from a body-less GET. Spread caller headers FIRST so these win.
    const commonHeaders: Record<string, string> = {
        ...headers,
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...buildApolloCsrfHeaders(operationName),
    };

    const response = await fetch(useGet ? `${url}?${searchString}` : url, {
        method: useGet ? 'GET' : 'POST',
        headers: commonHeaders,
        signal,
        ...(useGet
            ? {}
            : {
                  body: JSON.stringify({
                      extensions,
                      variables: params,
                      ...(operationName ? { operationName } : {}),
                  }),
              }),
    }).catch((err) => attachUrlToError(err, url) as never);

    // Parse defensively: a proxy or gateway can return a non-2xx body that's
    // either non-JSON (HTML error page) or JSON without an `errors[]` array.
    // Either shape used to resolve as `undefined` and let React Query treat a
    // hard failure as a successful empty response.
    let json: { data?: TData; errors?: ApiError['errors'] } = {};
    try {
        json = (await response.json()) as typeof json;
    } catch {
        // Non-JSON body — fall through to the status check below.
    }

    if (!response.ok || json.errors?.length) {
        // Mirror graphql-request's ClientError-ish shape so `handleRequestError` and
        // callers that read `error.response.errors[0].extensions.code` keep working.
        const message =
            json.errors?.[0]?.message ?? `GraphQL request failed with status ${response.status}`;
        const err = new Error(message) as Error & {
            response?: ApiError;
            requestUrl?: string;
        };
        err.response = { status: response.status, errors: json.errors };
        err.requestUrl = url;
        throw err;
    }

    return json.data as TData;
};

/**
 * Dedup the hash-miss warning at module scope — one signal per distinct
 * operation, not one per request. `executeRequest` fires on every render and
 * every SSR `queryFn`, so an unconditional warn would flood logs. Downgrading
 * to `debug` would lose the alarm; the Set keyed by `${appName}:${operationName}`
 * gives a "first sighting" signal bounded by codegen output size.
 *
 * `__resetWarnedMissingHashOperationsForTests` is a test-only reset hook.
 */
const warnedMissingHashOperations = new Set<string>();

const warnMissingHashOnce = (appName: MfeNames, operationName: string): void => {
    const key = `${appName}:${operationName}`;
    if (warnedMissingHashOperations.has(key)) return;
    warnedMissingHashOperations.add(key);
    appLogger.warn(
        'GraphQL request missing persisted-document hash; sending inline query. ' +
            'This operation is not codegen-generated, or its bundle is out of sync with the current sources.',
        { appName, operationName },
    );
};

/** @internal — test hook; do not import from application code. */
export const __resetWarnedMissingHashOperationsForTests = (): void => {
    warnedMissingHashOperations.clear();
};

/**
 * True when a persisted-hash request was rejected because the server manifest
 * does not contain the hash (HTTP 400 with the `PERSISTED_QUERY_NOT_FOUND`
 * extension code) — the MFE bundle-skew signature. Any error in the batch
 * qualifies: the server middleware rejects the whole request on a miss.
 *
 * The status check matters: `doPersistedRequest` also throws for GraphQL
 * errors on 200/5xx responses, where the operation may have (partially)
 * executed — retrying those inline could replay side effects. Only the
 * middleware's pre-execution 400 is known-safe to retry.
 */
const isPersistedQueryNotFoundError = (error: unknown): boolean => {
    const response = (error as UseQueryError | null)?.response;
    if (response?.status !== 400) return false;
    return Boolean(
        response.errors?.some(
            (graphQLError) => graphQLError?.extensions?.code === PERSISTED_QUERY_NOT_FOUND,
        ),
    );
};

/**
 * Dedup the hash-miss retry warning per operation, same rationale as
 * `warnMissingHashOnce`: the skew window after a deploy makes *every* request
 * from a stale bundle miss, and an unconditional warn would flood logs with
 * exactly the volume this retry exists to absorb. One "first sighting" per
 * operation keeps bundle skew visible in Datadog without the noise.
 */
const warnedPersistedRetryOperations = new Set<string>();

const warnPersistedRetryOnce = (appName: MfeNames, operationName: string): void => {
    const key = `${appName}:${operationName}`;
    if (warnedPersistedRetryOperations.has(key)) return;
    warnedPersistedRetryOperations.add(key);
    appLogger.warn(
        'Persisted query hash not in server manifest; retried with inline query. ' +
            'MFE bundle skew — this bundle was built from different sources than the serving manifest.',
        { appName, operationName },
    );
};

/** @internal — test hook; do not import from application code. */
export const __resetWarnedPersistedRetryOperationsForTests = (): void => {
    warnedPersistedRetryOperations.clear();
};

/** Records each client-side persisted-query miss and whether its inline retry recovered. */
const trackPersistedQueryNotFound = (context: {
    appName: MfeNames;
    operationName: string;
    hash: string;
    retrySucceeded: boolean;
}): void => {
    if (typeof window === UNDEFINED) return;
    import('@datadog/browser-rum')
        .then(({ datadogRum }) => {
            datadogRum.addAction('persisted_query_not_found', context);
        })
        .catch(() => {
            // RUM SDK unavailable — nothing to record.
        });
};

const PERSISTED_QUERY_BYPASS_TTL_MS = 30 * 60 * 1000;
const persistedRetryBypassUntil = new Map<string, number>();

const buildPersistedRetryBypassKey = (
    appName: MfeNames,
    operationName: string | undefined,
): string | null => {
    if (!operationName) return null;
    return `${appName}:${operationName}`;
};

const shouldBypassPersistedRetry = (key: string | null): boolean => {
    if (typeof window === UNDEFINED || !key) return false;
    const expiresAt = persistedRetryBypassUntil.get(key);
    if (expiresAt == null) return false;
    if (expiresAt <= Date.now()) {
        persistedRetryBypassUntil.delete(key);
        return false;
    }
    return true;
};

const markPersistedRetryBypass = (key: string | null): void => {
    if (typeof window === UNDEFINED || !key) return;
    persistedRetryBypassUntil.set(key, Date.now() + PERSISTED_QUERY_BYPASS_TTL_MS);
};

/** @internal — test hook; do not import from application code. */
export const __resetPersistedRetryBypassCacheForTests = (): void => {
    persistedRetryBypassUntil.clear();
};

type ExecuteRequestParams<TData, TVariables extends object = Record<string, unknown>> = {
    query: TypedDocumentNode<TData, TVariables>;
    params: TVariables;
    headers: Record<string, string>;
    appName: MfeNames;
    type: RequestType;
    isServer: boolean;
    relativePath: boolean;
    signal?: AbortSignal;
    /** The page request behind an SSR prefetch; its presence is what allows the in-process path. */
    req?: IncomingMessage;
};

/**
 * Inline-query transport: GET with spillover to POST when the URL would exceed
 * `MAX_GET_URL_LENGTH`. Serves documents without a persisted hash and the
 * hash-miss retry below.
 */
const executeInlineRequest = async <TData, TVariables extends object = Record<string, unknown>>(
    {
        query,
        params,
        headers,
        appName,
        type,
        isServer,
        relativePath,
    }: ExecuteRequestParams<TData, TVariables>,
    signal?: AbortSignal,
): Promise<TData | null> => {
    let requestType = type;
    if (type === REQUEST_TYPES.GET) {
        const estimatedUrlLength = estimateGetUrlLength<TData, TVariables>({
            query,
            params,
            appName,
            relativePath,
            isServer,
        });
        if (estimatedUrlLength > MAX_GET_URL_LENGTH) {
            requestType = REQUEST_TYPES.POST;
        }
    }

    if (requestType === REQUEST_TYPES.POST) {
        return doPostRequest<TData, TVariables>({
            appName,
            query,
            params,
            headers,
            isServer,
            signal,
        });
    }
    if (requestType === REQUEST_TYPES.GET) {
        return doGetRequest<TData, TVariables>({
            appName,
            query,
            params,
            headers,
            relativePath,
            isServer,
            signal,
        });
    }
    return null;
};

// Failures rethrow in the doPersistedRequest error shape so callers cannot tell the transports
// apart; there is no HTTP fallback because a self-request is the loop the recursion gate stops.
// The executor never throws by contract, so this is the only place that shapes its failures.
const executeInProcessRequest = async <TData, TVariables extends object>({
    query,
    params,
    headers,
    req,
    executor,
}: Pick<ExecuteRequestParams<TData, TVariables>, 'query' | 'params' | 'headers'> & {
    req: IncomingMessage;
    executor: InProcessGraphQLExecutor;
}): Promise<TData | null> => {
    const operationName = getOperationName(query);
    const result = await executor<TData>({
        document: query as DocumentNode,
        variables: params as Record<string, unknown>,
        operationName,
        headers,
        req,
    });
    if (result.errors?.length || result.status >= 400) {
        throw Object.assign(new Error(result.errors?.[0]?.message ?? 'GraphQL request failed'), {
            response: { status: result.status, errors: result.errors ?? [] },
            requestUrl: `in-process:${operationName ?? 'anonymous'}`,
        });
    }
    return result.data;
};

type PersistedAttemptParams<TData, TVariables extends object> = {
    request: ExecuteRequestParams<TData, TVariables>;
    hash: string;
    operationName: string | undefined;
    timeoutSignal: AbortSignal | undefined;
    timeoutError: () => Error;
};

/**
 * Persisted-hash request, with one inline retry when the serving manifest does not know the hash.
 */
const executePersistedRequest = async <TData, TVariables extends object>({
    request,
    hash,
    operationName,
    timeoutSignal,
    timeoutError,
}: PersistedAttemptParams<TData, TVariables>): Promise<TData | null> => {
    const { query, params, headers, appName, isServer, relativePath, signal } = request;
    const persistedRetryBypassKey = buildPersistedRetryBypassKey(appName, operationName);
    if (shouldBypassPersistedRetry(persistedRetryBypassKey) && isPrintableDocumentNode(query)) {
        return executeInlineRequest<TData, TVariables>(request, timeoutSignal);
    }
    try {
        const data = await doPersistedRequest<TData>({
            appName,
            hash,
            operationName,
            params: params as unknown as Record<string, unknown>,
            headers,
            relativePath,
            isServer,
            signal: signal ?? timeoutSignal,
        });
        // An abort while the body is being read is swallowed by the JSON parse and
        // would otherwise resolve as an empty success.
        if (timeoutSignal?.aborted) throw timeoutError();
        return data;
    } catch (error) {
        // A manifest miss (bundle skew after a deploy: this bundle's hash is
        // not in the serving manifest) is retriable — `embedHashInDocument`
        // keeps the full AST on the document, so the inline query is always
        // available. The inline path never sends the hash, so it cannot miss
        // again (no retry loop). Everything else propagates unchanged.
        if (!isPersistedQueryNotFoundError(error) || !isPrintableDocumentNode(query)) {
            throw error;
        }
        markPersistedRetryBypass(persistedRetryBypassKey);
        const resolvedOperationName = operationName ?? 'anonymous';
        warnPersistedRetryOnce(appName, resolvedOperationName);
        try {
            const result = await executeInlineRequest<TData, TVariables>(request, timeoutSignal);
            trackPersistedQueryNotFound({
                appName,
                operationName: resolvedOperationName,
                hash,
                retrySucceeded: true,
            });
            return result;
        } catch (retryError) {
            trackPersistedQueryNotFound({
                appName,
                operationName: resolvedOperationName,
                hash,
                retrySucceeded: false,
            });
            throw retryError;
        }
    }
};

/**
 * Persisted-hash request when the document has a codegen hash, otherwise fall back to inline
 * GET/POST.
 */
const executeNetworkRequest = async <TData, TVariables extends object = Record<string, unknown>>(
    request: ExecuteRequestParams<TData, TVariables>,
): Promise<TData | null> => {
    const { query, appName, isServer, relativePath, signal } = request;
    const hash = getPersistedHash(query);
    const operationName = getOperationName(query);
    // The SSR budget covers the whole operation, including a hash-miss inline retry. Only this
    // signal reaches the inline transport; a client cancel signal stays on the persisted path.
    const armed = armSsrTimeout(isServer, signal);
    const timeoutSignal = armed?.signal;
    const timeoutError = () =>
        buildTimeoutError({
            appName,
            operationName: operationName ?? 'anonymous',
            timeoutMs: armed?.timeoutMs ?? 0,
            requestUrl: `${getGraphQLRequestOrigin(appName, relativePath, isServer)}${getGraphQLPath(appName)}`,
        });

    // Every return awaits so the finally cannot clear the timer before the request settles.
    try {
        if (hash) {
            return await executePersistedRequest<TData, TVariables>({
                request,
                hash,
                operationName,
                timeoutSignal,
                timeoutError,
            });
        }

        // No persisted hash on the document — fall back to inline query transport.
        // graphql-codegen with `persistedDocuments` should attach a hash to every
        // generated node, so this branch is our alarm: an operation reaching it means
        // either the doc was hand-built or its bundle is out of sync with codegen.
        // Dedup at module scope (see `warnMissingHashOnce`) so we get one signal per
        // distinct operation instead of one per render — `executeRequest` fires in
        // the hot path and an unconditional warn would flood exactly the kind of
        // log-spam this PR set out to eliminate.
        warnMissingHashOnce(appName, operationName ?? 'anonymous');

        return await executeInlineRequest<TData, TVariables>(request, timeoutSignal);
    } catch (error) {
        if (armed?.signal.aborted && !isSsrTimeoutError(error)) throw timeoutError();
        throw error;
    } finally {
        armed?.clear();
    }
};

/**
 * Single choke point: a server prefetch whose target is the registered in-process owner runs on
 * that executor; everything else goes over the network.
 */
const executeRequest = async <TData, TVariables extends object = Record<string, unknown>>(
    request: ExecuteRequestParams<TData, TVariables>,
): Promise<TData | null> => {
    const { query, params, headers, appName, isServer, req } = request;
    const executor = isServer && req ? getInProcessExecutorFor(appName) : null;
    if (executor && req) {
        return executeInProcessRequest<TData, TVariables>({
            query,
            params,
            headers,
            req,
            executor,
        });
    }
    return executeNetworkRequest(request);
};

const createRequestQueryFn = <TData, TVariables extends object = Record<string, unknown>>({
    type = REQUEST_TYPES.GET,
    cacheKey,
    query,
    dataParams = null,
    appName = (process.env.NEXT_PUBLIC_APP_NAME as MfeNames) || 'app-shell',
    cancelOnSupersede = false,
    getHeaders,
}: Omit<requestParams<TData, TVariables>, 'requestOptions'>): ((context?: {
    signal?: AbortSignal;
}) => Promise<TData>) => {
    // Fail synchronously — before React Query stashes the queryFn — so the
    // offending call site appears in the stack trace.
    assertValidDocument(query, { cacheKey, appName });
    return async (context) => {
        const requestHeaders = createRequestHeaders(cacheKey, getHeaders?.());
        const params = processDataParams(dataParams) as TVariables;
        const result = await executeRequest<TData, TVariables>({
            query,
            params,
            headers: requestHeaders,
            appName,
            type,
            isServer: false,
            relativePath: true,
            // Don't destructure/read `context.signal` unless opted in: reading the getter
            // opts the query into RQ's cancel-on-unmount, so an in-flight request dropped
            // on unmount is reverted instead of populating the cache.
            signal: cancelOnSupersede ? context?.signal : undefined,
        });
        if (result === null) {
            throw new Error(`Invalid request type: ${type}`);
        }
        return result;
    };
};

export type RequestLogSeverity = 'error' | 'warn' | 'info';

// GraphQL error code returned for anonymous/logged-out reads (e.g.
// fullUserProfileProviderQuery). These are expected outcomes, not failures, and
// are the dominant source of false-`error` log noise — log them at `info`.
const EXPECTED_AUTH_ERROR_CODE = 'UNAUTHENTICATED';

// cacheKey identifiers for bootstrap/config reads that already fall back to safe
// defaults on failure (region, regionalConfiguration-<app>, translations-<locale>,
// featureFlags). Their failures are degraded-but-handled — log at `warn`. Each is
// listed explicitly (rather than relying on `region` to also cover
// `regionalConfiguration`) and matched as the exact key or a `<key>-` segment so
// unrelated keys that merely start with the same letters (e.g. `regionMatches`,
// `regionSetter`) are not silently downgraded.
const FALLBACK_COVERED_CACHE_KEYS = [
    'region',
    'regionalConfiguration',
    'translations',
    'featureFlags',
] as const;

// SEO metadata falls back to PO Editor copy then static defaults. Matched exactly,
// never by a `seo-` prefix, so unrelated future `seo-*` reads are not swept in.
const FALLBACK_COVERED_EXACT_CACHE_KEYS = ['seo'] as const;

const isFallbackCoveredCacheKey = (cacheKey: string): boolean =>
    FALLBACK_COVERED_EXACT_CACHE_KEYS.some((key) => cacheKey === key) ||
    FALLBACK_COVERED_CACHE_KEYS.some((key) => cacheKey === key || cacheKey.startsWith(`${key}-`));

// cacheKey identifiers for anonymous/bootstrap reads that fall back to a safe
// default when the request fails (e.g. `useFullUser` -> `ANON_PROFILE`). A
// failure that carries *no* GraphQL error code is a transport-level outcome —
// dominantly the browser aborting the in-flight request on navigation/unmount
// (surfaces upstream as HTTP 499), not a server fault. These aborts are the
// largest source of false-`error` noise for anonymous traffic, so code-less
// failures for these keys are logged at `info`. Coded failures (e.g.
// INTERNAL_SERVER_ERROR) are unaffected and still classify by code.
const ANONYMOUS_FALLBACK_READ_CACHE_KEYS = ['fullUserProfileProviderQuery'] as const;

// Exact match only — unlike `isFallbackCoveredCacheKey`, these keys are never
// suffixed (no `<key>-<locale>` variants), so prefix matching would risk
// silently downgrading an unrelated key that merely starts with the same text.
const isAnonymousFallbackReadCacheKey = (cacheKey: string): boolean =>
    ANONYMOUS_FALLBACK_READ_CACHE_KEYS.some((key) => cacheKey === key);

// 4xx statuses that are transient rather than deterministic: a retry can
// legitimately succeed after a request timeout (408) or rate-limit (429).
const TRANSIENT_4XX_STATUSES = new Set([408, 429]);

/**
 * True for a failure that will deterministically repeat within this session:
 * the server answered with a non-transient 4xx. The middlebox-stripped
 * BAD_REQUEST/MISSING_GRAPHQL_OPERATION storm (CBSENTWEB-5121) is the canonical
 * case — the proxy mangles every request the same way, so retrying can never
 * succeed and only multiplies request volume and log noise.
 */
export const isDeterministicClientError = (error: unknown): boolean => {
    const status = (error as UseQueryError | null | undefined)?.response?.status;
    return (
        typeof status === 'number' &&
        status >= 400 &&
        status < 500 &&
        !TRANSIENT_4XX_STATUSES.has(status)
    );
};

// React Query's own default attempt count, kept explicit so transient failures
// (5xx, transport errors) retain the pre-existing retry behaviour.
const MAX_TRANSIENT_RETRIES = 3;

const boundedRetry = (failureCount: number, error: UseQueryError): boolean =>
    !isDeterministicClientError(error) && failureCount < MAX_TRANSIENT_RETRIES;

/**
 * Default React Query options for every request hook. Callers' `requestOptions`
 * spread AFTER these, so an explicit option always wins.
 *
 * - `retry`: bounded, and never for deterministic 4xx failures (see
 *   `isDeterministicClientError`).
 * - Fallback-covered bootstrap reads additionally get `retryOnMount: false` and
 *   `refetchOnWindowFocus: false` — the circuit breaker for CBSENTWEB-5121.
 *   These reads degrade to safe defaults on failure, but they are observed by
 *   many components (nav, translations consumers, config readers), so with the
 *   React Query defaults every remount/refocus restarted a full retry cycle
 *   against an errored query: stuck sessions produced an unbounded 1–4s request
 *   loop (~117K "API request failed" logs / 15m in prod). `refetchOnReconnect`
 *   stays on — network recovery is the one signal genuinely worth a refetch.
 */
// Concrete option types (not `Pick<UseQueryOptions<…>>`): the option unions
// include query-typed callback forms, which makes them invariant in TData and
// unassignable across the hooks' differing generics. Booleans and an
// error-only retry predicate are valid members of every instantiation.
type RequestQueryDefaults = {
    retry: (failureCount: number, error: UseQueryError) => boolean;
    retryOnMount?: boolean;
    refetchOnWindowFocus?: boolean;
};

const buildRequestQueryDefaults = (cacheKey: string | null): RequestQueryDefaults => ({
    retry: boundedRetry,
    ...(cacheKey !== null &&
    (isFallbackCoveredCacheKey(cacheKey) || isAnonymousFallbackReadCacheKey(cacheKey))
        ? { retryOnMount: false, refetchOnWindowFocus: false }
        : {}),
});

// Stable component identifier so every request-layer failure can be filtered
// together in Datadog / New Relic, per the logging rule's `component` requirement.
const REQUEST_ERROR_COMPONENT = 'handleRequestError';

/**
 * Derive the log severity for a failed request from its GraphQL error code and
 * cacheKey. Reserves `error` for genuine failures (5xx, unexpected/missing
 * codes, no fallback); expected anonymous/logged-out reads and config reads with
 * fallbacks are downgraded so they no longer inflate `status:error`.
 */
export const classifyRequestErrorSeverity = (
    cacheKey: string | null,
    errorCode: string | undefined,
): RequestLogSeverity => {
    if (errorCode === EXPECTED_AUTH_ERROR_CODE) return 'info';
    if (cacheKey !== null && isFallbackCoveredCacheKey(cacheKey)) {
        return 'warn';
    }
    return 'error';
};

// Highest-severity-wins ranking so a batch is logged at the level of its most
// serious error.
const REQUEST_LOG_SEVERITY_RANK: Record<RequestLogSeverity, number> = {
    info: 0,
    warn: 1,
    error: 2,
};

/**
 * Resolve, in a single pass, both the highest severity across *all* GraphQL
 * error codes and the code that drove it. Classifying only the first error would
 * mask a genuine fault batched behind an expected UNAUTHENTICATED/fallback-covered
 * error, hiding real outages from `status:error`. Single source of truth so the
 * log level and the scalar `errorCode` logged by handleRequestError can never
 * disagree. Falls back to a code-less classification when there are no GraphQL
 * errors (e.g. network/HTTP failures).
 */
const resolveHighestRequestErrorSeverity = (
    cacheKey: string | null,
    errorCodes: ReadonlyArray<string | undefined>,
    isTransportFailure: boolean,
): { severity: RequestLogSeverity; code: string | undefined } => {
    if (errorCodes.length === 0) {
        // No GraphQL error body. Only a *genuine transport failure* (aborted or
        // failed fetch with no HTTP response) for an anonymous fallback read is
        // the benign browser-abort case we downgrade to `info`. A code-less
        // failure that still reached the server (e.g. a 5xx gateway/CDN error
        // page) must stay at `error`: on the paid brand it silently drops the
        // user to a safe default, so the outage has to remain visible in
        // status:error.
        if (isTransportFailure && cacheKey !== null && isAnonymousFallbackReadCacheKey(cacheKey)) {
            return { severity: 'info', code: undefined };
        }
        return { severity: classifyRequestErrorSeverity(cacheKey, undefined), code: undefined };
    }
    let severity: RequestLogSeverity = 'info';
    let code: string | undefined;
    let highestRank = -1;
    errorCodes.forEach((candidate) => {
        const candidateSeverity = classifyRequestErrorSeverity(cacheKey, candidate);
        const rank = REQUEST_LOG_SEVERITY_RANK[candidateSeverity];
        if (rank > highestRank) {
            highestRank = rank;
            severity = candidateSeverity;
            code = candidate;
        }
    });
    return { severity, code };
};

export const classifyRequestErrorSeverityFromCodes = (
    cacheKey: string | null,
    errorCodes: ReadonlyArray<string | undefined>,
    isTransportFailure = false,
): RequestLogSeverity =>
    resolveHighestRequestErrorSeverity(cacheKey, errorCodes, isTransportFailure).severity;

// Stable prefix for every request-failure log. Kept as a constant (and always
// the leading token of the message) so existing Datadog/New Relic queries that
// match on "API request failed" keep working after the message became dynamic.
const API_REQUEST_FAILED_MESSAGE = 'API request failed';

// A few cache keys interpolate raw user input (search queries) into the key,
// e.g. `search-suggestions-${query}`. The full key stays in the structured
// `cacheKey` field, but it must never reach the log *message*: patterns/monitors
// key on the message, so embedding free text would make it high-cardinality
// (defeating the clustering this change enables) and would leak user-typed text
// into the message string. For these keys we keep only the stable prefix in the
// message. Add new user-input-bearing key prefixes here as they are introduced.
const USER_INPUT_CACHE_KEY_PREFIXES = [
    'search-suggestions',
    'search-vod-content',
    'search-video-carousel',
] as const;

// Returns a low-cardinality, PII-safe rendering of the cacheKey for the message:
// user-input keys collapse to their stable prefix; every other key (locale/hub/
// region-suffixed or static) is low-cardinality and passes through unchanged.
const toMessageCacheKey = (cacheKey: string | null): string => {
    if (cacheKey === null) {
        return 'unknown';
    }
    return (
        USER_INPUT_CACHE_KEY_PREFIXES.find((prefix) => cacheKey.startsWith(`${prefix}-`)) ??
        cacheKey
    );
};

// Build a self-describing failure message that embeds the cacheKey (what failed)
// and, when present, the error code (why). A single static "API request failed"
// string collapses every failure into one opaque Datadog log-pattern cluster, so
// nobody can tell which request failed from the message alone. The cacheKey is
// normalized via toMessageCacheKey so the message stays low-cardinality and free
// of user input, while the structured fields (cacheKey/errorCode/errorCodes/
// errorCount) keep the full values for faceting.
const buildApiRequestFailedMessage = (
    cacheKey: string | null,
    errorCode: string | undefined,
): string => {
    const target = toMessageCacheKey(cacheKey);
    return errorCode
        ? `${API_REQUEST_FAILED_MESSAGE}: ${target} (${errorCode})`
        : `${API_REQUEST_FAILED_MESSAGE}: ${target}`;
};

// Dispatches the request-failure log at the classified level. Kept as a
// standalone helper so handleRequestError doesn't absorb the level branching.
// At `error` severity the raw throwable is forwarded so appLogger can capture a
// stack trace (it narrows the unknown value internally via toError); the warn/
// info breadcrumbs are expected outcomes and don't need one.
const logApiRequestFailure = ({
    severity,
    message,
    context,
    error,
}: {
    severity: RequestLogSeverity;
    message: string;
    context: Record<string, unknown>;
    error?: unknown;
}): void => {
    if (severity === 'warn') {
        appLogger.warn(message, context);
    } else if (severity === 'info') {
        appLogger.info(message, context);
    } else {
        appLogger.error(message, context, error);
    }
};

// Dedupes API error logs by identity, so React re-renders that surface the
// same error object do not double-log. Identity-based dedupe is the only safe
// strategy: a content-based signature (cacheKey + errorCode + errorCount)
// collapses distinct errors that happen to share those fields (e.g. a 500
// followed by a 503 with the same number of errors), dropping information we
// need in production. A WeakSet (not a single "last logged" slot) is required:
// when two or more failing queries alternate in one render pass (e.g. region +
// translations both erroring), a single slot lets each error evict the other,
// re-logging all of them on every render — this multiplied the CBSENTWEB-5121
// log storm. The WeakSet stays bounded because entries are released with the
// error objects themselves once React Query drops them.
const loggedErrors = new WeakSet<UseQueryError>();

// Collects every GraphQL error code from a failed request (empty when the error
// carries no GraphQL errors, e.g. network/HTTP failures).
const collectRequestErrorCodes = (error: UseQueryError | null): Array<string | undefined> =>
    (error?.response?.errors ?? []).map((graphQLError) => graphQLError?.extensions?.code);

// True only for a genuine transport-level failure: the fetch was aborted or
// never reached the server, so there is no HTTP `response` (graphql-request
// attaches one for any server reply, including 5xx/CDN pages). Requiring
// `requestUrl` (stamped by attachUrlToError) excludes synchronous pre-request
// throws, so only real aborts are downgraded to info.
const isTransportLevelFailure = (error: UseQueryError | null): boolean =>
    error?.response == null && typeof error?.requestUrl === 'string';

const handleRequestError = (error: UseQueryError | null, cacheKey: string | null) => {
    if (error && typeof window !== UNDEFINED) {
        handleDataLayerError(cacheKey);

        const isDuplicate = loggedErrors.has(error);

        if (!isDuplicate) {
            loggedErrors.add(error);
            const errorCodes = collectRequestErrorCodes(error);
            // Emit the scalar `errorCode` (the code that drove the severity)
            // alongside the richer `errorCodes` array so existing Datadog/New
            // Relic facets keyed on the pre-PR scalar keep matching. For the
            // common single-error response this equals the previous first-error
            // value.
            const { severity, code: errorCode } = resolveHighestRequestErrorSeverity(
                cacheKey,
                errorCodes,
                isTransportLevelFailure(error),
            );
            logApiRequestFailure({
                severity,
                message: buildApiRequestFailedMessage(cacheKey, errorCode),
                context: {
                    component: REQUEST_ERROR_COMPONENT,
                    cacheKey,
                    errorCode,
                    errorCodes,
                    errorCount: error?.response?.errors?.length,
                },
                error,
            });
        }

        if (
            error?.response?.errors?.[0]?.extensions?.code ===
                GRAPHQL_CODE_THAT_CLEARS_LOGIN_SESSION &&
            hasCookie(LOGIN_SESSION_COOKIE)
        ) {
            deleteCookie(LOGIN_SESSION_COOKIE);
        }
    }
};

export const buildRequestQueryKey = <TVariables extends object>(
    cacheKey: string | null,
    dataParams?: TVariables | null,
) => (dataParams ? [cacheKey, dataParams] : [cacheKey]);

export const useRequest = <TData, TVariables extends object = Record<string, unknown>>({
    type = REQUEST_TYPES.GET,
    cacheKey,
    query,
    dataParams = null,
    appName = (process.env.NEXT_PUBLIC_APP_NAME as MfeNames) || 'app-shell',
    requestOptions = {},
    cancelOnSupersede = false,
    getHeaders,
}: requestParams<TData, TVariables>) => {
    const queryKey = buildRequestQueryKey(cacheKey, dataParams);
    const results = useQuery<TData, UseQueryError>({
        queryKey,
        queryFn: createRequestQueryFn<TData, TVariables>({
            type,
            cacheKey,
            query,
            dataParams,
            appName,
            cancelOnSupersede,
            getHeaders,
        }),
        ...buildRequestQueryDefaults(cacheKey),
        ...requestOptions,
    });

    if (results.error) handleRequestError(results.error, cacheKey);
    // Returns all data from useQuery
    // Docs: https://tanstack.com/query/latest/docs/framework/react/reference/useQuery
    return results;
};

export const useManyRequests = (requests: Array<requestParams<unknown, object>>) => {
    const queries = useQueries({
        queries: requests.map((requestConfig) => {
            const {
                type = REQUEST_TYPES.GET,
                cacheKey,
                query,
                dataParams = null,
                appName = (process.env.NEXT_PUBLIC_APP_NAME as MfeNames) || 'app-shell',
                requestOptions = {},
            } = requestConfig;

            const queryKey = buildRequestQueryKey(cacheKey, dataParams);

            return {
                queryKey,
                queryFn: createRequestQueryFn({
                    type,
                    cacheKey,
                    query,
                    dataParams,
                    appName,
                } as Omit<requestParams<unknown, object>, 'requestOptions'>),
                ...buildRequestQueryDefaults(cacheKey),
                ...requestOptions,
            };
        }),
    });

    // Handle errors for any of the queries
    queries.forEach((result, index) => {
        const { error } = result;
        const requestConfig = requests[index];

        handleRequestError(error, requestConfig.cacheKey);
    });

    return queries;
};

type PaginatedRequestParams<T, TVariables extends object, TItems> = {
    type?: RequestType;
    cacheKey: string | null;
    query: TypedDocumentNode<T, TVariables>;
    buildDataParams: (pageParam: number) => TVariables;
    toRequestPage: (data: T, pageNumber: number) => RequestPage<TItems>;
    appName?: MfeNames;
    initialPages: number[];
    requestOptions?: Omit<UseQueryOptions<RequestPage<TItems>, UseQueryError>, 'queryKey'>;
};

export const usePaginatedRequest = <T, DT extends object, TItems>({
    type = REQUEST_TYPES.GET,
    cacheKey,
    query,
    buildDataParams,
    toRequestPage,
    appName = (process.env.NEXT_PUBLIC_APP_NAME as MfeNames) || 'app-shell',
    initialPages,
    requestOptions = {},
}: PaginatedRequestParams<T, DT, TItems>) => {
    // Capture the very first `initialPages` so `resetPages()` and the auto-reset below
    // both return to a stable anchor even if the caller changes the prop on later renders.
    const initialPagesRef = useRef(initialPages);

    // Discriminate cache identity by the params for page 1 — same shape `useQueries`
    // builds its queryKey from, so when this changes, every other page's key changes too.
    // `hashKey` is React Query's own stable serializer; using it here keeps our identity
    // check consistent with how the cache itself partitions queries.
    const paramsKey = hashKey([cacheKey, buildDataParams(1)]);

    // `pageNumbers` is kept inside a tuple with the params identity that produced it. When
    // the caller passes a new `buildDataParams` (e.g. a category change in
    // `useLiveTvChannels`), the previous render's accumulated page list (`[1..N]` from the
    // background fill) would otherwise be replayed against the new cache key — useQueries
    // would issue N parallel POSTs and the EPG would render a stale-data → loading-blank
    // → new-data flicker. Resetting synchronously to `initialPages` during render lets
    // React discard the in-progress render and re-run with `pageNumbers = initialPages`,
    // so only one query fires against the new key and there's no intermediate misleading
    // render of data from the previous key.
    const [state, setState] = useState<{ key: string; pages: number[] }>(() => ({
        key: paramsKey,
        pages: initialPagesRef.current,
    }));

    if (state.key !== paramsKey) {
        setState({ key: paramsKey, pages: initialPagesRef.current });
    }

    const pageNumbers = state.key === paramsKey ? state.pages : initialPagesRef.current;

    const queries = useQueries({
        queries: pageNumbers.map((pageNumber) => {
            const dataParams = buildDataParams(pageNumber);
            return {
                queryKey: [cacheKey, dataParams],
                queryFn: async (): Promise<RequestPage<TItems>> => {
                    const res = await createRequestQueryFn<T, DT>({
                        type,
                        cacheKey,
                        query,
                        dataParams,
                        appName,
                    })();
                    return toRequestPage(res, pageNumber);
                },
                ...buildRequestQueryDefaults(cacheKey),
                ...requestOptions,
            };
        }),
    });

    const fetchPage = useCallback((pageNumber: number) => {
        setState((prev) => {
            if (prev.pages.includes(pageNumber)) {
                return prev;
            }
            return {
                ...prev,
                pages: [...prev.pages, pageNumber].sort((a, b) => a - b),
            };
        });
    }, []);

    // Manual reset for callers that want to drop accumulated pages on a UI event,
    // independent of the params-identity auto-reset above.
    const resetPages = useCallback(() => {
        setState((prev) => ({ ...prev, pages: initialPagesRef.current }));
    }, []);

    // Process queries to extract pages data
    const pages: RequestPage<TItems>[] = [];
    let error: UseQueryError | null = null;

    queries.forEach((query, index) => {
        const pageNumber = pageNumbers[index];
        if (query.isLoading) {
            pages.push({
                pageNumber,
                isFetching: true,
            });
        } else if (query.error) {
            error = query.error as unknown as UseQueryError;
            handleRequestError(error, cacheKey);
        } else if (query.data) {
            pages.push(query.data);
        }
    });

    // Sort pages by page number to maintain order
    pages.sort((a, b) => a.pageNumber - b.pageNumber);

    // On-demand refresh for single-page callers (e.g. useLiveTvChannels({channelSlug})). Joins an
    // in-flight fetch (cancelRefetch: false) instead of restarting it. refetch() resolves with the
    // previous data on failure, so rethrow — callers would otherwise get stale data as fresh.
    const refetchFirstPageQuery = queries[0]?.refetch;
    const refetchFirstPage = useCallback(async (): Promise<RequestPage<TItems> | undefined> => {
        const result = await refetchFirstPageQuery?.({ cancelRefetch: false });
        if (result?.isError) throw result.error;
        return result?.data;
    }, [refetchFirstPageQuery]);

    return {
        pages,
        fetchPage,
        resetPages,
        refetchFirstPage,
        isLoading: queries.every((q) => q.isLoading),
        error,
    };
};

export interface prefetchPaginatedRequestParams<T, TVariables extends object, TItems> {
    type?: RequestType;
    cacheKey: string | null;
    query: TypedDocumentNode<T, TVariables>;
    buildDataParams: (pageParam: number) => TVariables;
    toRequestPage: (data: SuccessResult<T>, pageNumber: number) => RequestPage<TItems>;
    appName?: MfeNames;
    req: IncomingMessage;
    pages: number[];
}
/**
 * Region context forwarded from an SSR request to the internal GraphQL request.
 * `region-in-path` carries the fully-resolved, geo-aware region (getRegion:
 * x-client-region wins; a marketing-region URL like /latam/ falls back to its main
 * country). Because the internal request no longer carries the original URL, this
 * resolved value is what keeps SSR prefetches (EPG channels, categories, …) scoped
 * to the right region. Single source of truth shared by prefetchRequest and
 * prefetchPaginatedRequest — region is the only context the GraphQL route needs to
 * resolve the backend region/host.
 */
const getRegionForwardHeaders = (
    req?: IncomingMessage,
    region?: string,
): Record<string, string> => {
    const forwardedRegion = region || (req && getRegion(req));
    return forwardedRegion ? { [REGION_IN_PATH]: forwardedRegion } : {};
};

export const createSsrPrefetchRequestHeaders = (
    cacheKey: string | null,
    req: IncomingMessage,
    region?: string,
): Record<string, string> => {
    const clientRegion = getHeaderValue(req, CLIENT_REGION_HEADER);
    const userAgent = getHeaderValue(req, 'user-agent');
    const acceptLanguage = getHeaderValue(req, 'accept-language');
    const forwardedFor = getHeaderValue(req, 'x-forwarded-for');
    const originalForwardedFor = getHeaderValue(req, 'x-original-forwarded-for');
    const cnetForwardedFor = getHeaderValue(req, 'x-cnet-forwarded-for');
    const forwardedHost = getForwardedHost(req);
    const cookie = getHeaderValue(req, 'cookie');
    const automationPlatform = getHeaderValue(req, AUTOMATION_PLATFORM_HEADER);
    const cmsOverrides = currentEnvMirrorsProd()
        ? null
        : new URLSearchParams(req.url?.split('?')[1] ?? '').get('cms');

    const headers = createRequestHeaders(cacheKey, {
        ...(cookie && { Cookie: cookie }),
        ...(cmsOverrides && { [CMS_OVERRIDES_HEADER]: cmsOverrides }),
        ...getRegionForwardHeaders(req, region),
        ...(clientRegion && { [CLIENT_REGION_HEADER]: clientRegion }),
        ...(userAgent && { 'user-agent': userAgent }),
        ...(acceptLanguage && { 'accept-language': acceptLanguage }),
        ...(forwardedHost && { 'x-forwarded-host': forwardedHost }),
        ...(forwardedFor && { 'x-forwarded-for': forwardedFor }),
        ...(originalForwardedFor && { 'x-original-forwarded-for': originalForwardedFor }),
        ...(cnetForwardedFor && { 'x-cnet-forwarded-for': cnetForwardedFor }),
        ...(automationPlatform && { [AUTOMATION_PLATFORM_HEADER]: automationPlatform }),
    });

    return headers;
};

export const prefetchPaginatedRequest = <T, TVariables extends object, TItems>({
    type = REQUEST_TYPES.GET,
    cacheKey,
    query,
    buildDataParams,
    toRequestPage,
    appName = (process.env.NEXT_PUBLIC_APP_NAME as MfeNames) || 'app-shell',
    req,
    pages,
}: prefetchPaginatedRequestParams<T, TVariables, TItems>) => {
    // Guard once at builder time — every paginated queryFn shares this document.
    assertValidDocument(query, { cacheKey, appName });
    const queries = pages.map((pageNumber) => {
        const dataParams = buildDataParams(pageNumber);
        return {
            queryKey: [cacheKey, dataParams],
            queryFn: async () => {
                const requestHeaders = createSsrPrefetchRequestHeaders(cacheKey, req!);
                const params = processDataParams(dataParams) as TVariables;
                const result = await executeRequest<T, TVariables>({
                    query,
                    params,
                    headers: requestHeaders,
                    appName,
                    type,
                    isServer: true,
                    relativePath: false,
                    req,
                });
                if (result === null) return undefined;
                return toRequestPage(result as SuccessResult<T>, pageNumber);
            },
        };
    });

    return queries;
};

export type StandardPrefetchDefinition<TData = unknown> = Required<
    Pick<UsePrefetchQueryOptions<TData | null, UseQueryError, TData | null>, 'queryKey'>
> & {
    queryFn: () => Promise<TData | null>;
};
export const prefetchRequest = <TData, TVariables extends object = Record<string, unknown>>({
    type = REQUEST_TYPES.GET,
    cacheKey,
    query,
    dataParams = null,
    appName = (process.env.NEXT_PUBLIC_APP_NAME as MfeNames) || 'app-shell',
    req,
    region,
}: prefetchRequestParams<TData, TVariables>): StandardPrefetchDefinition<TData> => {
    // Refuse to build a prefetch when the document isn't usable. Runs at SSR
    // getServerSideProps time so failures land in server logs with a stack trace.
    assertValidDocument(query, { cacheKey, appName });
    const queryKey = buildRequestQueryKey(cacheKey, dataParams);

    return {
        queryKey,
        queryFn: async () => {
            const requestHeaders = createSsrPrefetchRequestHeaders(cacheKey, req, region);

            // Route through `executeRequest` so persisted-hash and legacy paths
            // share GET→POST spillover logic and headers.
            const params = processDataParams(dataParams) as TVariables;
            return executeRequest<TData, TVariables>({
                query,
                params,
                headers: requestHeaders,
                appName,
                type,
                isServer: true,
                relativePath: false,
                req,
            });
        },
    };
};

export const fetchClient = <TData, TVariables extends object = Record<string, unknown>>({
    type = REQUEST_TYPES.GET,
    cacheKey,
    query,
    dataParams = null,
    appName = (process.env.NEXT_PUBLIC_APP_NAME as MfeNames) || 'app-shell',
}: Omit<requestParams<TData, TVariables>, 'requestOptions'>) => {
    const queryKey = buildRequestQueryKey(cacheKey, dataParams);
    return {
        queryKey,
        queryFn: createRequestQueryFn<TData, TVariables>({
            type,
            cacheKey,
            query,
            dataParams,
            appName,
        }),
    };
};

export const emitError = (cacheKey: string | null) => {
    if (typeof window !== UNDEFINED) {
        handleDataLayerError(cacheKey);
    }
};

type InfiniteRequestParams<
    TData,
    TQueryKeyParams extends object,
    TVariables extends TQueryKeyParams,
    TPageParam,
> = GraphQLInfiniteRequestCore<TData, TQueryKeyParams, TVariables, TPageParam> & {
    requestOptions?: InfiniteRequestOptions<TData, TPageParam>;
};

export const useInfiniteRequest = <
    TData,
    TQueryKeyParams extends object,
    TVariables extends TQueryKeyParams,
    TPageParam,
>({
    type = REQUEST_TYPES.GET,
    cacheKey,
    query,
    queryKeyParams,
    buildDataParams,
    getNextPageParam,
    initialPageParam,
    appName = (process.env.NEXT_PUBLIC_APP_NAME as MfeNames) || 'app-shell',
    requestOptions = {},
}: InfiniteRequestParams<TData, TQueryKeyParams, TVariables, TPageParam>) => {
    const resolvedQueryKeyParams = queryKeyParams ?? buildDataParams(initialPageParam);
    const queryKey = buildRequestQueryKey(cacheKey, resolvedQueryKeyParams);

    const results = useInfiniteQuery<
        TData,
        UseQueryError,
        InfiniteData<TData, TPageParam>,
        readonly unknown[],
        TPageParam
    >({
        queryKey,
        queryFn: async ({ pageParam }) =>
            createRequestQueryFn<TData, TVariables>({
                type,
                cacheKey,
                query,
                dataParams: buildDataParams(pageParam as TPageParam),
                appName,
            })(),
        initialPageParam,
        getNextPageParam,
        ...requestOptions,
    });

    if (results.error) {
        handleRequestError(results.error, cacheKey);
    }

    return results;
};

export type PrefetchInfiniteRequestParams<
    TData,
    TQueryKeyParams extends object,
    TVariables extends TQueryKeyParams,
    TPageParam,
> = GraphQLInfiniteRequestCore<TData, TQueryKeyParams, TVariables, TPageParam> & {
    req: IncomingMessage;
    pages?: number;
};

export type InfinitePrefetchDefinition<
    TData = unknown,
    TPageParam = unknown,
> = FetchInfiniteQueryOptions<
    TData | null,
    UseQueryError,
    TData | null,
    readonly unknown[],
    TPageParam
>;

export type PrefetchQueryDefinition = StandardPrefetchDefinition | InfinitePrefetchDefinition;

export const toPrefetchQueryDefinition = (definition: unknown): PrefetchQueryDefinition => {
    const list = Array.isArray(definition) ? definition : [definition];
    if (list.some((d) => typeof (d as { queryFn?: unknown })?.queryFn !== 'function')) {
        throw new Error('toPrefetchQueryDefinition(): expected a query definition with queryFn.');
    }
    return definition as PrefetchQueryDefinition;
};

export const prefetchInfiniteRequest = <
    TData,
    TQueryKeyParams extends object,
    TVariables extends TQueryKeyParams,
    TPageParam,
>({
    type = REQUEST_TYPES.GET,
    cacheKey,
    query,
    queryKeyParams,
    buildDataParams,
    getNextPageParam,
    initialPageParam,
    appName = (process.env.NEXT_PUBLIC_APP_NAME as MfeNames) || 'app-shell',
    req,
    pages = 1,
}: PrefetchInfiniteRequestParams<
    TData,
    TQueryKeyParams,
    TVariables,
    TPageParam
>): InfinitePrefetchDefinition<TData, TPageParam> => {
    assertValidDocument(query, { cacheKey, appName });
    const resolvedQueryKeyParams = queryKeyParams ?? buildDataParams(initialPageParam);
    const queryKey = buildRequestQueryKey(cacheKey, resolvedQueryKeyParams);

    return {
        queryKey,
        queryFn: async ({ pageParam }) => {
            const requestHeaders = createSsrPrefetchRequestHeaders(cacheKey, req);
            const params = processDataParams(
                buildDataParams(pageParam as TPageParam),
            ) as TVariables;
            return executeRequest<TData, TVariables>({
                query,
                params,
                headers: requestHeaders,
                appName,
                type,
                isServer: true,
                relativePath: false,
                req,
            });
        },
        initialPageParam,
        getNextPageParam: getNextPageParam as GetNextPageParamFunction<TPageParam, TData | null>,
        pages,
    };
};
